import { FC, ReactElement } from 'react';
import {
  Chip,
  CircularProgress,
  Tooltip,
  SxProps,
  Theme,
} from '@mui/material';
import {
  CheckCircleOutlineRounded,
  ErrorOutlineRounded,
  HourglassEmptyRounded,
  RadioButtonUncheckedRounded,
  ScheduleRounded,
  TaskAltRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { TaskRo } from 'src/app/services/api.generated';
import { isActiveTaskState } from 'src/utils/taskProgressStreamUtils';

export const statusChipBaseSx: SxProps<Theme> = {
  height: 22,
  borderRadius: '6px',
  fontWeight: 600,
  fontSize: '0.75rem',
  letterSpacing: '0.01em',
  '& .MuiChip-label': { px: 1 },
  '& .MuiChip-icon': {
    color: 'inherit',
    fontSize: 14,
    ml: '6px',
    mr: '-2px',
  },
};

/**
 * Small icon in front of the label. Decorative: the text label carries the
 * meaning, so status never depends on colour alone.
 */
export const statusChipIcons: Record<string, ReactElement> = {
  waiting: <HourglassEmptyRounded aria-hidden />,
  // Spinner for work that is running right now.
  running: (
    <CircularProgress
      aria-hidden
      size={10}
      thickness={6}
      color="inherit"
      sx={{ flexShrink: 0 }}
    />
  ),
  done: <CheckCircleOutlineRounded aria-hidden />,
  analysed: <TaskAltRounded aria-hidden />,
  todo: <RadioButtonUncheckedRounded aria-hidden />,
  queued: <ScheduleRounded aria-hidden />,
  error: <ErrorOutlineRounded aria-hidden />,
};

/** Status chip colours from the theme (`palette.status`). */
export const statusToneSx = (
  tone: keyof Theme['palette']['status']
): SxProps<Theme> => ({
  ...statusChipBaseSx,
  color: `status.${tone}.text`,
  bgcolor: `status.${tone}.bg`,
  border: '1px solid',
  borderColor: `status.${tone}.border`,
});

/** Grey chip for states that are waiting on something. */
export const neutralStatusChipSx: SxProps<Theme> = {
  ...statusChipBaseSx,
  color: 'customGrey.grey1',
  bgcolor: 'customGrey.grey3',
  border: '1px solid',
  borderColor: 'divider',
};

const delayedChipSx: SxProps<Theme> = statusToneSx('warning');

type AnalysisChipProps = {
  state: TaskRo['state'];
  processingProgress?: number;
  stuckStage?: TaskRo['stuckStage'];
};

type OutputChipProps = {
  outputState: TaskRo['outputState'];
  stuckStage?: TaskRo['stuckStage'];
};

const DelayedChip: FC = () => {
  const { t } = useTranslation();
  return (
    <Tooltip
      title={t('status.delayedHelp', {
        defaultValue:
          'Taking longer than usual. We have been notified and are looking into it.',
      })}
    >
      <Chip
        size="small"
        icon={statusChipIcons.queued}
        label={t('status.delayed', { defaultValue: 'Delayed' })}
        variant="outlined"
        sx={delayedChipSx}
      />
    </Tooltip>
  );
};

export const TaskAnalysisStatusChip: FC<AnalysisChipProps> = ({
  state,
  processingProgress,
  stuckStage,
}) => {
  const { t } = useTranslation();

  if (stuckStage === 'image' || stuckStage === 'ai') {
    return <DelayedChip />;
  }

  const pct =
    processingProgress !== undefined && isActiveTaskState(state)
      ? ` ${Math.round(processingProgress)}%`
      : '';

  let label = '';
  let icon: ReactElement | undefined;
  let sx: SxProps<Theme> = statusChipBaseSx;

  switch (state) {
    case 'ERROR':
      label = t('status.error', { defaultValue: 'Error' });
      icon = statusChipIcons.error;
      sx = statusToneSx('error');
      break;
    case 'CREATE_IMAGES':
    case 'PENDING':
      label = `${t('legend.waiting')}${pct}`;
      icon = statusChipIcons.waiting;
      sx = neutralStatusChipSx;
      break;
    case 'PREPARING':
    case 'CREATING_IMAGES':
    case 'PROCESSING':
    case 'PARTIAL_READY':
      label = `${t('legend.inProgress')}${pct}`;
      icon = statusChipIcons.running;
      sx = statusToneSx('info');
      break;
    case 'READY':
      label = t('status.analysed', { defaultValue: 'Analysed' });
      icon = statusChipIcons.analysed;
      sx = statusToneSx('primary');
      break;
    case 'READY_WITH_ERRORS':
      return (
        <Tooltip
          title={t('status.failedHelp', {
            defaultValue:
              'Some pages could not be analysed. Open the document to review what is available.',
          })}
        >
          <Chip
            size="small"
            icon={statusChipIcons.error}
            label={t('status.failed', { defaultValue: 'Failed' })}
            variant="outlined"
            sx={statusToneSx('error')}
          />
        </Tooltip>
      );
    default:
      return null;
  }

  return (
    <Chip size="small" icon={icon} label={label} variant="outlined" sx={sx} />
  );
};

export const TaskOutputStatusChip: FC<OutputChipProps> = ({
  outputState,
  stuckStage,
}) => {
  const { t } = useTranslation();

  if (stuckStage === 'output') {
    return <DelayedChip />;
  }

  let label = '';
  let icon: ReactElement | undefined;
  let sx: SxProps<Theme> = statusChipBaseSx;

  switch (outputState) {
    case 'GENERATING_OUTPUT':
      label = t('status.generatingOutput', { defaultValue: 'Anonymizing' });
      icon = statusChipIcons.running;
      sx = statusToneSx('info');
      break;
    case 'NOT_GENERATED':
      label = t('status.notGenerated', { defaultValue: 'Not anonymized' });
      icon = statusChipIcons.todo;
      sx = statusToneSx('warning');
      break;
    case 'GENERATE_OUTPUT':
      label = t('status.queued', { defaultValue: 'Queued' });
      icon = statusChipIcons.queued;
      sx = neutralStatusChipSx;
      break;
    case 'GENERATED_OUTPUT':
      label = t('status.generated', { defaultValue: 'Anonymized' });
      icon = statusChipIcons.done;
      sx = statusToneSx('success');
      break;
    case 'OUTDATED_OUTPUT':
      // The redactions changed after the export: anonymize again.
      label = t('exportOutdatedStatus', { defaultValue: 'Export outdated' });
      icon = statusChipIcons.todo;
      sx = statusToneSx('warning');
      break;
    case 'ERROR':
      label = t('status.error', { defaultValue: 'Error' });
      icon = statusChipIcons.error;
      sx = statusToneSx('error');
      break;
    default:
      return null;
  }

  return (
    <Chip size="small" icon={icon} label={label} variant="outlined" sx={sx} />
  );
};
